import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthUser from '../hooks/useAuthUser';
import { OrganizationMembers } from '../admin/components/organizations/OrganizationMembers.tsx';
import {
  fetchOrgAdminMembers,
  removeOrgAdminMember,
  updateOrgAdminMemberEmail,
  updateOrgAdminMemberRole,
} from '../request/orgAdmin/orgAdmin.api.ts';
import { fetchOrgAdminRoles } from '../request/membership/orgUsers.api.ts';
import { ORG_ROLE_OPTIONS } from '../admin/orgRoles.ts';
import { DEFAULT_PATHS } from '../routing/paths.ts';

function getStoredOrganizationId(): string {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem('organization_id') ?? '';
}

const OrgAdminPage: React.FC = () => {
  const organizationId = getStoredOrganizationId();
  const currentUser = useAuthUser(null);
  const currentUserId = currentUser?.id != null ? Number(currentUser.id) : null;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['org-admin', 'members', organizationId],
    queryFn: () => fetchOrgAdminMembers(organizationId),
    enabled: Boolean(organizationId),
  });

  const { data: catalogRoles = [] } = useQuery({
    queryKey: ['org-admin', 'roles'],
    queryFn: fetchOrgAdminRoles,
    enabled: Boolean(organizationId),
  });

  const roleOptions = useMemo(() => {
    if (catalogRoles.length === 0) {
      return ORG_ROLE_OPTIONS;
    }
    return catalogRoles.map((role) => ({
      value: role.key,
      label: role.name,
    }));
  }, [catalogRoles]);

  const invalidateMembers = () => {
    queryClient.invalidateQueries({ queryKey: ['org-admin', 'members', organizationId] });
  };

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      updateOrgAdminMemberRole(userId, role),
    onSuccess: async (_data, variables) => {
      invalidateMembers();

      const isSelf =
        currentUserId != null && String(variables.userId) === String(currentUserId);
      const lostOrgAdmin = isSelf && variables.role !== 'org_admin';

      if (lostOrgAdmin) {
        // Refresh role/module access so guards and nav drop org-admin UI.
        await queryClient.invalidateQueries({
          queryKey: ['org-membership', 'module-access'],
        });
        toast.success('Your role was updated. Org admin access is no longer available.');
        navigate(DEFAULT_PATHS.TENANT_LANDING, { replace: true });
        return;
      }

      toast.success('Role updated successfully');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Failed to update role';
      toast.error(message);
    },
  });

  const updateEmailMutation = useMutation({
    mutationFn: ({ userId, email }: { userId: string; email: string }) =>
      updateOrgAdminMemberEmail(userId, email),
    onSuccess: () => {
      toast.success('Email updated successfully');
      invalidateMembers();
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Failed to update email';
      toast.error(message);
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => removeOrgAdminMember(userId),
    onSuccess: () => {
      toast.success('Member removed successfully');
      invalidateMembers();
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Failed to remove member';
      toast.error(message);
    },
  });

  const canModifyMember = useMemo(() => {
    return (memberRole: string, memberUserId: number) => {
      if (memberRole !== 'org_admin') return true;
      return currentUserId != null && memberUserId === currentUserId;
    };
  }, [currentUserId]);

  if (!organizationId) {
    return (
      <div className="max-w-3xl mx-auto px-8 py-12 text-sm text-gray-600">
        Select an organization to manage members.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
            <Users className="h-5 w-5 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Organization Members</h1>
            <p className="text-sm text-gray-600">
              View members, change emails or roles, or remove users from your organization.
            </p>
          </div>
        </div>
      </div>

      <OrganizationMembers
        members={members}
        users={[]}
        isLoading={isLoading}
        roleOptions={roleOptions}
        onRemoveUser={removeMemberMutation.mutateAsync}
        onUpdateRole={({ userId, payload }) =>
          updateRoleMutation.mutateAsync({ userId, role: payload.role })
        }
        onUpdateEmail={({ userId, email }) =>
          updateEmailMutation.mutateAsync({ userId, email })
        }
        canEditRole={(member) => canModifyMember(member.role, member.user_id)}
        canEditEmail={(member) => canModifyMember(member.role, member.user_id)}
        canRemove={(member) =>
          !member.is_default && canModifyMember(member.role, member.user_id)
        }
      />
    </div>
  );
};

export default OrgAdminPage;
